<script setup>
import { onMounted, ref } from 'vue'
import { downloadReportPdf, getReportResults, getReportTemplates } from '../../api/reportApi.js'
import AppAlert from '../../shared/components/AppAlert.vue'
import ResultHistoryTable from './components/ResultHistoryTable.vue'
import { canDownloadPdf } from './historyUtils.js'
import { createTemplateSelectionMap, findDefaultTemplate, includeTemplateInFilename } from '../../report/reportTemplateUtils.js'

const PAGE_SIZE = 20
const results = ref([])
const page = ref(0)
const totalElements = ref(0)
const totalPages = ref(0)
const isLoading = ref(false)
const error = ref('')
const downloadingReportIds = ref(new Set())
const templates = ref([])
const selectedTemplateIds = ref({})
const templateErrors = ref({})
const isTemplateLoading = ref(true)

function syncTemplateSelections() {
  const defaultTemplateId = findDefaultTemplate(templates.value)?.templateId || ''
  selectedTemplateIds.value = createTemplateSelectionMap(
    results.value,
    selectedTemplateIds.value,
    defaultTemplateId
  )
}

async function loadTemplates() {
  isTemplateLoading.value = true
  try {
    const response = await getReportTemplates()
    templates.value = response.templates || []
    syncTemplateSelections()
  } catch (cause) {
    error.value = cause.message || '리포트 종류를 불러오지 못했습니다.'
  } finally {
    isTemplateLoading.value = false
  }
}

async function loadHistory(targetPage = 0) {
  if (isLoading.value) return
  isLoading.value = true
  error.value = ''
  try {
    const response = await getReportResults(targetPage, PAGE_SIZE)
    results.value = response.content || []
    syncTemplateSelections()
    page.value = response.page
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
  } catch (cause) {
    error.value = cause.message || '결과이력을 불러오지 못했습니다.'
  } finally {
    isLoading.value = false
  }
}

async function downloadPdf(result) {
  if (!canDownloadPdf(result) || downloadingReportIds.value.has(result.reportId)) return
  const templateId = selectedTemplateIds.value[result.reportId]
  if (!templateId) return
  downloadingReportIds.value = new Set(downloadingReportIds.value).add(result.reportId)
  templateErrors.value = { ...templateErrors.value, [result.reportId]: '' }
  try {
    const download = await downloadReportPdf(result.reportId, templateId)
    const url = URL.createObjectURL(download.blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = includeTemplateInFilename(
      download.filename || `건강자산_보장분석_${result.uniqueNumber}.pdf`,
      templateId
    )
    anchor.click()
    window.setTimeout(() => URL.revokeObjectURL(url), 1000)
  } catch (cause) {
    templateErrors.value = {
      ...templateErrors.value,
      [result.reportId]: cause.message || 'PDF를 생성하지 못했습니다.'
    }
  } finally {
    const remainingIds = new Set(downloadingReportIds.value)
    remainingIds.delete(result.reportId)
    downloadingReportIds.value = remainingIds
  }
}

function changeTemplate(reportId, templateId) {
  selectedTemplateIds.value = { ...selectedTemplateIds.value, [reportId]: templateId }
  templateErrors.value = { ...templateErrors.value, [reportId]: '' }
}

onMounted(async () => {
  await Promise.all([loadTemplates(), loadHistory()])
  syncTemplateSelections()
})
</script>

<template>
  <section class="history-page">
    <div class="history-heading">
      <div>
        <p class="home-kicker">결과이력</p>
        <h1>결과이력</h1>
        <p class="page-lead">지금까지 산출한 건강 × 보장 분석 결과입니다. 완료된 결과는 PDF로 받을 수 있습니다.</p>
      </div>
      <div class="history-summary"><div><span>전체 결과</span><b>{{ totalElements }}건</b></div></div>
    </div>

    <AppAlert v-if="error">{{ error }}</AppAlert>

    <ResultHistoryTable
      :results="results"
      :page="page"
      :page-size="PAGE_SIZE"
      :total-elements="totalElements"
      :total-pages="totalPages"
      :is-loading="isLoading"
      :downloading-report-ids="downloadingReportIds"
      :templates="templates"
      :selected-template-ids="selectedTemplateIds"
      :template-errors="templateErrors"
      :is-template-loading="isTemplateLoading"
      @download="downloadPdf"
      @template-change="changeTemplate"
      @page-change="loadHistory"
    />
  </section>
</template>
